/* One-time client bootstrap once a session exists: load the floor, start the
   sync loop, and keep the floor warm. Idempotent. Stores that care about live
   events (chat, notifications) register their own sync subscriptions when first
   used; bootstrap makes sure they're instantiated. */
import { sync } from '@/lib/sync'
import { startLiveness, stopLiveness } from '@/lib/liveness'
import { useFloor } from '@/stores/floor'
import { useChat } from '@/stores/chat'
import { useNotify } from '@/stores/notify'
import { useMentions } from '@/stores/mentions'
import { useSuggestions } from '@/stores/suggestions'
import { useMeetings } from '@/stores/meetings'
import { usePresence } from '@/stores/presence'
import { useAdmin } from '@/stores/admin'
import { useTransitions } from '@/stores/transitions'
import { useSession } from '@/stores/session'
import { isAuthError } from '@/types/matrix'

let booted = false
let refreshTimer: ReturnType<typeof setInterval> | null = null
let stateDebounce: ReturnType<typeof setTimeout> | null = null

// The token died under us (e.g. the homeserver was reset): stop everything and
// bounce to the login screen instead of retrying a dead token forever. Guarded
// so a burst of failing requests only triggers one sign-out.
function onAuthError(): void {
  if (!booted) return // already torn down
  teardownClient()
  useSession().forceLogout('Your session expired — please sign in again.')
}

export async function bootClient(): Promise<void> {
  if (booted) return
  booted = true

  const floor = useFloor()
  const chat = useChat()
  const notify = useNotify()
  const mentions = useMentions()

  // Live consumers subscribe to the sync stream + load read markers.
  chat.init()
  notify.init()
  mentions.init()
  useTransitions().init() // follow attachment watchers
  void notify.boot()
  void mentions.boot()
  void useSuggestions().boot() // pending meeting suggestions (toast + rail badge)
  void useMeetings().boot() // my meeting summaries (Mentions ▸ Meetings + rail badge)

  try {
    await floor.loadFloor()
  } catch (e) {
    // A stale token shows up here first: don't strand the user on a broken
    // floor — sign them back out cleanly.
    if (isAuthError(e)) return onAuthError()
    throw e
  }

  // Resolve our role on the floor (shapes the Admin nav; enforced server-side).
  void useAdmin().loadRole()

  // Project our presence once on boot so others see us on the floor.
  void usePresence().push()

  // Catch up on chat mentions that landed while we were offline (bounded scan of
  // readable floor rooms back to a watermark; first run just starts the clock).
  void mentions.catchUpChat()

  // A state change in a joined room → debounced floor refresh.
  sync.on('state', () => {
    if (stateDebounce) clearTimeout(stateDebounce)
    stateDebounce = setTimeout(() => void floor.refresh(), 400)
  })

  // An invite may be a team-room door opening after our knock.
  sync.on('invite', (roomId) => void floor.onInvite(roomId))

  // A dead token surfaces as a failing /sync — sign out instead of looping.
  sync.on('error', (e) => { if (isAuthError(e)) onAuthError() })

  sync.start()

  // Heartbeat so the engine knows we have Atrium open (drives the floor's
  // "around but not in a room" view).
  startLiveness()

  // Heat/occupancy of rooms we're NOT joined to isn't carried by /sync, so keep
  // the floor warm on a slow poll (the rest is real-time via sync).
  if (refreshTimer) clearInterval(refreshTimer)
  refreshTimer = setInterval(() => { if (floor.active) void floor.refresh() }, 12000)
}

export function teardownClient(): void {
  booted = false
  sync.stop()
  stopLiveness()
  if (refreshTimer) clearInterval(refreshTimer)
  refreshTimer = null
}
